// src/components/messaging/MentorshipHeader.js
//
// Sprint Vidal M2B commit B4b — Header léger affiché au-dessus du chat
// quand la conversation est de type 'mentoring'. Fournit le contexte
// produit (sujet, objectif, date cible, niveau engagement, durée active,
// noms participants) + bouton "Objectif atteint" pour clôturer.
//
// Data via GET /mentoring/conversations/:id/mentorship-header (B4a).
// Pas de fallback texte vide ("Pas d'objectif défini") : si une ligne
// n'a pas de data, on la cache. Mieux qu'un faux contenu.
//
// Clôture via ConfirmationModal obligatoire (changement d'état fort
// active → completed). Appel PUT /mentoring/mentorships/:id/complete
// existant (envoie 2 notifs feedback_request via flow back B1).

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CButton } from '@coreui/react'
import { useSelector } from 'react-redux'
import { get as apiGet, put as apiPut } from '../../utils/apiClient'
import { useNotification } from '../../contexts/NotificationContext'
import { formatDateI18n } from '../../utils/i18nFormatters'
import Avatar from '../shared/Avatar'
import ConfirmationModal from './ConfirmationModal'

// labelKey résolu via t() au rendu (clés mentorship_header.engagement_*).
const ENGAGEMENT_LABEL_KEYS = {
  one_off: 'mentoring:mentorship_header.engagement_one_off',
  light: 'mentoring:mentorship_header.engagement_light',
  ongoing: 'mentoring:mentorship_header.engagement_ongoing',
}

// Format date locale-aware via date-fns ('PPP' = date longue localisée).
// Le backend envoie un format YYYY-MM-DD (goalTargetDate) ou DATETIME ISO
// (completedAt, sliced à 10 chars) → parseISO gère les deux. Remplace
// l'ancien helper formatDateFR (Intl UTC) ; le rendu reste une date "wall
// clock" sans décalage car YYYY-MM-DD est interprété en heure locale.
const formatDateLong = (dateStr, lang) => {
  if (!dateStr) return null
  return formatDateI18n(dateStr, 'PPP', lang) || null
}

const MentorshipHeader = ({ conversationId }) => {
  const { t, i18n } = useTranslation()
  const [header, setHeader] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [completing, setCompleting] = useState(false)
  const currentUser = useSelector((state) => state.user?.user)
  const { addToast } = useNotification()

  // Fetch helper réutilisable (used by initial mount + post-complete refetch).
  const fetchHeader = useCallback(async () => {
    if (!conversationId) {
      setHeader(null)
      return
    }
    try {
      const data = await apiGet(`/mentoring/conversations/${conversationId}/mentorship-header`)
      setHeader(data)
    } catch {
      // Fail silent : 404 (non-participant ou pas de mentorship) / 403 / 5xx
      // → on n'affiche simplement pas le header
      setHeader(null)
    }
  }, [conversationId])

  // Pattern identique à B3b : reset SYNCHRONE au début pour éviter stale UI
  // sur switch rapide entre conversations mentoring (sinon header A visible
  // pendant la latence fetch B).
  useEffect(() => {
    setHeader(null)
    let cancelled = false
    if (!conversationId) return
    apiGet(`/mentoring/conversations/${conversationId}/mentorship-header`)
      .then((data) => {
        if (!cancelled) setHeader(data)
      })
      .catch(() => {
        if (!cancelled) setHeader(null)
      })
    return () => {
      cancelled = true
    }
  }, [conversationId])

  const handleConfirmComplete = async () => {
    if (!header?.mentorshipId || completing) return
    setCompleting(true)
    try {
      await apiPut(`/mentoring/mentorships/${header.mentorshipId}/complete`)
      addToast(
        t('mentoring:mentorship_header.toast_closed_title'),
        t('mentoring:mentorship_header.toast_closed_body'),
        'success',
      )
      setShowConfirmModal(false)
      // Refetch : status devient 'completed', canMarkComplete=false →
      // le bouton disparaît + badge "Clôturé" apparaît. Conv reste ouverte.
      await fetchHeader()
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || t('mentoring:mentorship_header.error_generic')
      addToast(t('mentoring:mentorship_header.toast_error_title'), msg, 'danger')
    } finally {
      setCompleting(false)
    }
  }

  if (!header) return null

  // "Other participant" : du point de vue du user courant, c'est celui qu'il
  // mentore ou celui qui le mentore. Fallback "Participant supprimé" si
  // l'autre a été anonymisé (ON DELETE SET NULL côté DB).
  const isMentor = currentUser?.id === header.mentor?.id
  const other = isMentor ? header.mentee : header.mentor
  const otherName = other
    ? `${other.firstName || ''} ${other.lastName || ''}`.trim() ||
      t('mentoring:mentorship_header.default_participant')
    : t('mentoring:mentorship_header.deleted_participant')

  // Formatage pieces UI
  const goalDate = formatDateLong(header.goalTargetDate, i18n.language)
  const engagementLabel = header.engagementLevel
    ? t(ENGAGEMENT_LABEL_KEYS[header.engagementLevel])
    : null
  const isCompleted = header.status === 'completed' || header.completedAt != null
  // completedAt est DATETIME ISO ("2026-05-25T15:30:00.000Z") → slice pour
  // garder juste la date avant format long.
  const completedDate = header.completedAt
    ? formatDateLong(String(header.completedAt).slice(0, 10), i18n.language)
    : null

  // "Démarré [aujourd'hui / hier / il y a N jours]" — pluriel via i18next.
  const daysActive = header.daysActive
  let startedText
  if (daysActive <= 0) {
    startedText = t('mentoring:mentorship_header.started_today')
  } else if (daysActive === 1) {
    startedText = t('mentoring:mentorship_header.started_yesterday')
  } else {
    startedText = t('mentoring:mentorship_header.started_days_ago', { count: daysActive })
  }

  return (
    <>
      <div
        className="mentorship-header"
        style={{
          background: 'rgba(0,0,0,0.03)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          padding: '10px 14px',
          fontSize: '0.85rem',
          lineHeight: 1.4,
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
              {other && <Avatar user={other} size={22} showCareerStatus={false} />}
              <strong>{t('mentoring:mentorship_header.title', { name: otherName })}</strong>
              {isCompleted && (
                <span
                  className="badge bg-success bg-opacity-25 text-success"
                  style={{ fontSize: '0.7rem', fontWeight: 500 }}
                >
                  {completedDate
                    ? t('mentoring:mentorship_header.closed_on', { date: completedDate })
                    : t('mentoring:mentorship_header.closed')}
                </span>
              )}
            </div>
            {header.topic && (
              <div className="text-muted">
                {t('mentoring:mentorship_header.topic', { topic: header.topic })}
              </div>
            )}
            {header.goalText && (
              <div className="text-muted">
                {goalDate
                  ? t('mentoring:mentorship_header.goal_with_date', {
                      goal: header.goalText,
                      date: goalDate,
                    })
                  : t('mentoring:mentorship_header.goal', { goal: header.goalText })}
              </div>
            )}
            <div className="text-muted">
              {engagementLabel ? `${engagementLabel} · ` : ''}
              {startedText}
            </div>
          </div>
          {header.canMarkComplete && (
            <CButton
              size="sm"
              color="success"
              variant="outline"
              onClick={() => setShowConfirmModal(true)}
              disabled={completing}
              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('mentoring:mentorship_header.mark_complete')}
            </CButton>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => !completing && setShowConfirmModal(false)}
        onConfirm={handleConfirmComplete}
        title={t('mentoring:mentorship_header.confirm_title')}
        message={t('mentoring:mentorship_header.confirm_message')}
        confirmText={t('mentoring:mentorship_header.confirm_button')}
        cancelText={t('common:cancel')}
        loading={completing}
        confirmButtonClass="btn-success"
      />
    </>
  )
}

export default MentorshipHeader
